/**
 * The device's record of how far each learner has got in each module.
 *
 * The *rules* are pure and live in `#/lib/moduleRun`; what is here is the store —
 * read it, write it, and tell every screen that is drawing it when it changes —
 * plus the hook the screens use. It exists as a store rather than as a hook-local
 * `useState` for the reason the classrooms' own store exists: the run is written in
 * one place and read in another. The player records a capsule; the module's screen
 * draws *4 of 6* and offers *Continue*; the card in a feed draws the same sentence.
 * A second copy of the answer in any of them is how a learner finishes a capsule and
 * is told they have not.
 *
 * Storage is `#/state/persisted` under `moduleRuns` — the same place the classrooms
 * keep a device-local world, and the same reason: there is no endpoint for a run yet
 * (`app.bsky.feed.getCapsulePlays` is not implemented), so the run is remembered
 * rather than fetched. `read()` merges the stored rows over the defaults so an older
 * stored state is read as *nothing started* rather than as an error.
 */
import {useCallback, useMemo, useSyncExternalStore} from 'react'

import {
  findModuleRun,
  MODULE_RUNS_DEFAULT,
  moduleRunProgress,
  type ModuleRunProgress,
  type ModuleRunsState,
  upsertModuleRun,
} from '#/lib/moduleRun'
import * as persisted from '#/state/persisted'
import {useSession} from '#/state/session'

const listeners = new Set<() => void>()

function read(): ModuleRunsState {
  return persisted.get('moduleRuns') ?? MODULE_RUNS_DEFAULT
}

/*
 * The snapshot is cached until something writes.
 *
 * `useSyncExternalStore` compares snapshots by identity, so a `read()` that built a
 * new object every time would re-render every subscriber on every event — and, worse,
 * would look like a change when nothing had changed.
 */
let cache: ModuleRunsState | null = null

function getSnapshot(): ModuleRunsState {
  if (!cache) cache = read()
  return cache
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  /*
   * Another tab may have written. The app's own writes go through `write` below and
   * are announced here directly, because on native there is nothing watching the
   * disk — `persisted.onUpdate` is a no-op there.
   */
  const unsubscribe = persisted.onUpdate('moduleRuns', () => {
    cache = null
    for (const notify of listeners) notify()
  })
  return () => {
    listeners.delete(listener)
    unsubscribe()
  }
}

function write(next: ModuleRunsState): void {
  cache = next
  void persisted.write('moduleRuns', next)
  for (const notify of listeners) notify()
}

/**
 * A learner's run of one module.
 *
 * `items` is the playlist as the screen knows it, so the progress is counted against
 * the capsules that are still *in* the module: a place the creator removed must not
 * leave a learner at five of six for ever.
 */
export function useModuleRun({
  moduleUri,
  items,
}: {
  moduleUri: string | undefined
  items: readonly {uri: string}[]
}): {
  /** The capsules of this playlist whose questions have been finished. */
  done: string[]
  progress: ModuleRunProgress
  /** A capsule's questions were finished: remember it on this device. */
  markDone: (capsuleUri: string) => void
  /** Start the module over — the whole run, not one capsule. */
  clear: () => void
} {
  const {currentAccount} = useSession()
  /* Signed out, the run belongs to the device and to nobody in particular. */
  const did = currentAccount?.did ?? ''
  const runs = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)

  const done = useMemo(
    () => findModuleRun(runs, moduleUri ?? '', did)?.done ?? [],
    [did, moduleUri, runs],
  )

  const progress = useMemo(
    () => moduleRunProgress({items, done}),
    [done, items],
  )

  const writeDone = useCallback(
    (nextDone: string[]) => {
      if (!moduleUri) return
      write(
        upsertModuleRun(getSnapshot(), {
          moduleUri,
          did,
          done: nextDone,
          updatedAt: new Date().toISOString(),
        }),
      )
    },
    [did, moduleUri],
  )

  const markDone = useCallback(
    (capsuleUri: string) => {
      if (done.includes(capsuleUri)) return
      writeDone([...done, capsuleUri])
    },
    [done, writeDone],
  )

  const clear = useCallback(() => writeDone([]), [writeDone])

  return {done, progress, markDone, clear}
}
